/**
 * Shared shapes + constants for a movable/collapsible panel grid (GridStack) — the
 * machinery LCDashboards keeps private, rebuilt here so other dashboards (first:
 * Accounting Dashboards) can compose it. LC still runs its own copy; see
 * docs/plans/2026-09-09-accounting-dashboards-shell-design.md.
 */
import type { PanelTrashMap } from './panelLayoutTrash';
import { readPanelTrash } from './panelLayoutTrash';

/** Stored GridStack position + collapse/lock state for one panel. */
export interface PanelLayoutItem {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;          // live grid height (1 when collapsed)
  collapsed?: boolean;
  locked?: boolean;   // when true, the tile can't be dragged or resized
  expandedH?: number; // height to restore to when expanded
}

/** A panel the page knows how to render, with its starting footprint for auto-flow. */
export interface PanelDef {
  key: string;
  title: string;
  defaultW: number;
  defaultH: number;
}

export type PanelLayouts = { [workspaceId: string]: PanelLayoutItem[] };

/** GridStack sizing for a tile grid: fine (24 cols / 40px rows) so tiles hug content. */
export const PANEL_COLUMNS = 24;
export const PANEL_CELL_HEIGHT = 40;
export const PANEL_MARGIN = 4;
export const PANEL_DEFAULT_W = 12;
export const PANEL_DEFAULT_H = 6;

/**
 * Layout version stamped into the saved blob. Bump it when the grid geometry changes
 * (columns / cell height); a blob from another version is ignored on load so tiles are
 * never placed with the wrong unit. (LC scaled v1→v2 in place; a new page has no legacy.)
 */
export const PANEL_GRID_VERSION = 2;

/** Class DashboardTile puts on its header bar; GridStack drags the tile by it. */
export const PANEL_DRAG_HANDLE_CLASS = 'panel-drag-handle';

export interface PanelGridOptions {
  column: number;
  cellHeight: number;
  margin: number;
  /** CSS selector GridStack uses as the drag handle (DashboardTile's header bar). */
  handle: string;
}

export const TILE_GRID_OPTIONS: PanelGridOptions = {
  column: PANEL_COLUMNS,
  cellHeight: PANEL_CELL_HEIGHT,
  margin: PANEL_MARGIN,
  handle: `.${PANEL_DRAG_HANDLE_CLASS}`,
};

/** The persisted user_settings value. */
export interface PanelSettingValue {
  panelLayouts: PanelLayouts;
  version: number;
  panelTrash: PanelTrashMap<PanelLayoutItem>;
}

/**
 * The items a workspace renders: the user's saved geometry when they have any, else the
 * authored default, and an auto-flow (left→right, wrapping) below whatever was placed for
 * any panel neither source knows. A panel added after a user arranged their board never
 * lands on the default's spot — that spot may be occupied — it flows in under everything.
 * A non-empty saved layout always wins over the fallback, even if none of its keys are current —
 * the fallback is not consulted at all in that case.
 */
export const resolvePanelItems = (
  defs: PanelDef[],
  saved: PanelLayoutItem[] | undefined,
  fallback: PanelLayoutItem[] | undefined,
  columns: number = PANEL_COLUMNS,
): PanelLayoutItem[] => {
  const base = saved && saved.length > 0 ? saved : (fallback ?? []);
  const baseByKey = new Map(base.filter((i) => i.w >= 1 && i.h >= 1).map((i) => [i.key, i]));
  const defKeys = new Set(defs.map((d) => d.key));
  let cursorX = 0;
  let cursorY = 0;
  let rowH = 0;
  baseByKey.forEach((i, key) => {
    if (defKeys.has(key)) cursorY = Math.max(cursorY, i.y + i.h);
  });
  return defs.map((def) => {
    const placed = baseByKey.get(def.key);
    if (placed) return { ...placed };
    if (cursorX + def.defaultW > columns) {
      cursorX = 0;
      cursorY += rowH;
      rowH = 0;
    }
    const item: PanelLayoutItem = { key: def.key, x: cursorX, y: cursorY, w: def.defaultW, h: def.defaultH };
    cursorX += def.defaultW;
    rowH = Math.max(rowH, def.defaultH);
    return item;
  });
};

/**
 * Parse a persisted blob. `layouts` is null when there is nothing usable (absent, malformed,
 * or authored for another grid version) — the caller keeps its code default. Everything from
 * another grid version is discarded — layouts and trash alike — since neither is placeable in
 * the current units. Within a current-version blob, a workspace whose value is not an array
 * is dropped so a corrupt entry can't reach the grid; the well-formed workspaces are kept.
 */
export const readPanelSetting = (
  value: unknown,
  version: number = PANEL_GRID_VERSION,
): { layouts: PanelLayouts | null; trash: PanelTrashMap<PanelLayoutItem> } => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { layouts: null, trash: {} };
  const v = value as Partial<PanelSettingValue>;
  if (v.version !== version) return { layouts: null, trash: {} };
  const trash = readPanelTrash<PanelLayoutItem>(v.panelTrash);
  const raw = v.panelLayouts;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { layouts: null, trash };
  const layouts: PanelLayouts = Object.fromEntries(
    Object.entries(raw).filter(([, items]) => Array.isArray(items)),
  );
  if (Object.keys(layouts).length === 0) return { layouts: null, trash };
  return { layouts, trash };
};
